import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarPlus, Repeat, Check, AlertTriangle, Ban, Clock } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useSession } from '../lib/SessionContext';
import ScheduleBlocks from '../components/ScheduleBlocks';
import PatientCancelModal from '../components/PatientCancelModal';
import { t, formatTime, formatDate } from '../i18n';
import { dayKey, dayRange, addDaysToKey, weekStartKey, fromInputValue } from '../lib/practiceTime';
import { weekColumns } from '../lib/weekGrid';
import { placeOptions, placeLabel } from '../lib/availability';
import { patientMayCancel } from '../lib/cancellation';
import { isoWeekday, seriesDays, MAX_SERIES } from '../lib/recurrence';
import {
  Card, Button, Select, Field, Alert, Spinner, EmptyState, Modal, Input,
} from '../components/ui';

// Sessions that hold a time on the week, drawn over the grid.
const ACTIVE_STATUSES = ['requested', 'scheduled', 'waiting', 'in_session'];

/**
 * Weekly booking calendar, shared by patients and professionals.
 *
 * A patient books one slot for one of their family members, with one of that
 * person's professionals; a professional can also lay out a standing weekly
 * series ("every Thursday at 16:00 for twelve weeks"). Both call the same
 * server function, so one set of rules governs availability, approval and
 * pricing — the calendar only displays what the server already decided.
 */

const BookingView = () => {
  const { isPatient, therapistId } = useSession();

  // A day key (YYYY-MM-DD) on the practice's calendar, not a browser Date: the
  // grid comes back laid out in the practice's zone, and is cut into days the
  // same way here (lib/practiceTime.js).
  const [weekStart, setWeekStart] = useState(() => weekStartKey(dayKey()));
  // One entry per accepted treatment: a person and one of their professionals.
  // Someone seeing two professionals appears twice on a patient's list.
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [slots, setSlots] = useState([]);
  // The professional's own sessions this week; null on a patient's reading.
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [booking, setBooking] = useState(null);
  // A time typed rather than picked off the grid. The grid steps by one session
  // from the day's start, which is a rhythm plenty of professionals do not
  // keep — 9:15, then 10, then 11 is nobody's arithmetic progression.
  const [customAt, setCustomAt] = useState('');
  // A standing series (0069): the days it falls on, every how many weeks,
  // until when. The day of the session being booked is always one of them,
  // so extraDays holds only the others.
  const [repeats, setRepeats] = useState(false);
  const [extraDays, setExtraDays] = useState([]);
  const [intervalWeeks, setIntervalWeeks] = useState('1');
  const [until, setUntil] = useState('');
  const [busy, setBusy] = useState(false);
  // A patient's own session whose cancel dialog is open, and what it did (0057).
  const [cancelling, setCancelling] = useState(null);
  const [cancelNotice, setCancelNotice] = useState(null);

  const selectedPatient = patients.find((patient) => patient.key === selectedPatientId);
  const onlineLocation = locations.find((location) => location.is_online) ?? null;

  // Patients pick from their own family's accepted treatments; professionals
  // from their own accepted patients.
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // A patient's pairs come from my_treatments() (0046), which returns their
      // own family's part of each treatment; a professional reads their own.
      const query = isPatient
        ? supabase.rpc('my_treatments')
        : supabase
          .from('treatments')
          .select('id, dependent_id, therapist_id, status, dependents ( first_name, last_name )')
          .eq('status', 'approved')
          .eq('therapist_id', therapistId);

      // The name shown beside each patient. A patient cannot read the therapists
      // table (migration 0035), so theirs come from my_professionals().
      const [{ data }, { data: locationRows }, { data: professionals }] = await Promise.all([
        query,
        supabase.from('locations').select('id,name,is_online').eq('is_active', true).order('name'),
        isPatient ? supabase.rpc('my_professionals') : Promise.resolve({ data: [] }),
      ]);
      const byId = Object.fromEntries((professionals ?? []).map((row) => [row.id, row]));

      // The function names the person on the row; the table embeds them.
      const rows = (data ?? [])
        .filter((row) => row.status === 'approved' && (isPatient || row.dependents))
        .map((row) => ({
          key: row.id,
          dependentId: row.dependent_id,
          therapistId: row.therapist_id,
          first_name: isPatient ? row.first_name : row.dependents.first_name,
          last_name: isPatient ? row.last_name : row.dependents.last_name,
          therapists: byId[row.therapist_id] ?? null,
        }))
        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, 'es'));

      setPatients(rows);
      setLocations(locationRows ?? []);
      if (rows.length) setSelectedPatientId((current) => current || rows[0].key);
      if (locationRows?.length) setLocationId((current) => current || locationRows[0].id);
      setLoading(false);
    };
    load();
  }, [isPatient, therapistId]);

  // Whose week is on screen: the chosen treatment's professional, or — for a
  // professional with an empty roster — their own.
  const agendaTherapistId = isPatient ? (selectedPatient?.therapistId ?? null) : therapistId;

  const loadSlots = useCallback(async () => {
    if (!agendaTherapistId) { setSlots([]); setSessions(null); return; }
    setLoadingSlots(true);
    setError(null);
    const weekFrom = dayRange(weekStart).start;
    const weekTo = dayRange(addDaysToKey(weekStart, 7)).start;
    const [slotResult, sessionResult] = await Promise.all([
      supabase.rpc('available_slots', {
        p_therapist_id: agendaTherapistId,
        p_from: weekStart,
        p_to: addDaysToKey(weekStart, 6),
      }),
      // The grid holds only what working hours generate, so a session booked
      // off it had nowhere to appear; sessions are drawn from the sessions
      // themselves (lib/weekGrid.js). A professional's are their own. A
      // patient's are their family's, from my_sessions, so they can cancel one
      // from here (0057) — never anyone else's.
      isPatient
        ? supabase.rpc('my_sessions', { p_from: weekFrom })
        : supabase
          .from('appointments')
          .select('id, starts_at, ends_at, status, dependents ( first_name, last_name ), locations ( name )')
          .eq('therapist_id', agendaTherapistId)
          .in('status', ACTIVE_STATUSES)
          .gte('starts_at', weekFrom)
          .lt('starts_at', weekTo)
          .order('starts_at'),
    ]);
    const failure = slotResult.error ?? sessionResult.error;
    if (failure) setError(failure.message);
    setSlots(slotResult.data ?? []);
    setSessions(isPatient
      ? (sessionResult.data ?? [])
        .filter((row) => row.therapist_id === agendaTherapistId
          && ACTIVE_STATUSES.includes(row.status)
          && Date.parse(row.starts_at) < Date.parse(weekTo))
        // The shape the grid draws from, as the table's embeds give it.
        .map((row) => ({
          ...row,
          dependents: { first_name: row.first_name, last_name: row.last_name },
          locations: row.location_name ? { name: row.location_name } : null,
        }))
      : (sessionResult.data ?? []));
    setLoadingSlots(false);
  }, [agendaTherapistId, weekStart, isPatient]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const days = useMemo(() => weekColumns({ weekStart, slots, sessions }), [slots, sessions, weekStart]);

  // Where the session being booked can be (0044). A patient picks among what
  // the time offers; a professional can also put it anywhere else, as with a
  // typed time.
  const placeChoices = useMemo(() => {
    const offered = booking && !booking.custom ? placeOptions(booking, onlineLocation) : [];
    const others = isPatient && booking && !booking.custom ? [] : locations
      .filter((location) => !offered.some((option) => option.id === location.id))
      .map((location) => ({ id: location.id, name: location.name, online: location.is_online }));
    return [...offered, ...others].map((option) => ({
      ...option,
      label: option.online ? t('availability.online') : `${t('availability.inPerson')} · ${option.name}`,
    }));
  }, [booking, onlineLocation, locations, isPatient]);

  // The day the session being booked falls on, whether it came off the grid or
  // was typed, and the series that would hang off it. Counted here so the
  // dialog can say how many turnos it is about to create, and refuse a series
  // the database would refuse anyway (lib/recurrence.js).
  const bookingInstant = booking?.custom
    ? fromInputValue(customAt)
    : (booking?.slot_start ? new Date(booking.slot_start) : null);
  const baseDay = bookingInstant && !Number.isNaN(bookingInstant.getTime())
    ? dayKey(bookingInstant)
    : null;
  const baseWeekday = baseDay ? isoWeekday(baseDay) : null;

  const weekdays = useMemo(
    () => [...new Set([...(baseWeekday ? [baseWeekday] : []), ...extraDays])].sort((a, b) => a - b),
    [baseWeekday, extraDays],
  );
  const seriesDates = useMemo(
    () => (repeats && baseDay ? seriesDays(baseDay, weekdays, Number(intervalWeeks) || 1, until) : []),
    [repeats, baseDay, weekdays, intervalWeeks, until],
  );
  // The seven chips are labelled off the week the session is in, so the day
  // names come from the same formatter as the grid above them.
  const weekdayKeys = useMemo(
    () => (baseDay
      ? Array.from({ length: 7 }, (_, index) => addDaysToKey(weekStartKey(baseDay), index))
      : []),
    [baseDay],
  );

  /** What the dialog says the series comes to, or why it comes to nothing. */
  const seriesMessage = (() => {
    if (!repeats || !until) return null;
    if (seriesDates.length > MAX_SERIES) return t('booking.repeatTooMany');
    if (seriesDates.length === 0) return t('booking.repeatNoDates');
    return t('booking.repeatCount', { count: seriesDates.length });
  })();

  /** The dialog and everything it was holding. */
  const closeBooking = () => {
    setBooking(null);
    setRepeats(false);
    setExtraDays([]);
    setIntervalWeeks('1');
    setUntil('');
  };

  // The place starts at the first one the time offers: its sede, or online when
  // that is all it offers.
  const openSlot = (slot) => {
    setBooking(slot);
    setLocationId(placeOptions(slot, onlineLocation)[0]?.id ?? '');
  };

  const confirmBooking = async () => {
    // A typed time is the practice's wall clock, whatever clock the browser is
    // on; the grid already hands back an instant.
    const startsAt = booking.custom
      ? (fromInputValue(customAt)?.toISOString() ?? null)
      : booking.slot_start;

    if (!startsAt || Number.isNaN(Date.parse(startsAt))) {
      setError(t('booking.customTimeInvalid'));
      return;
    }

    // A series is checked before it is sent: an end date that yields nothing,
    // or more turnos than one series may hold, is a question for the screen.
    if (repeats) {
      if (!until) { setError(t('booking.repeatUntilRequired')); return; }
      if (seriesDates.length === 0) { setError(t('booking.repeatNoDates')); return; }
      if (seriesDates.length > MAX_SERIES) { setError(t('booking.repeatTooMany')); return; }
    }

    setBusy(true);
    setError(null);

    if (repeats) {
      const { data, error: seriesError } = await supabase.rpc('book_series', {
        p_dependent_id: selectedPatient.dependentId,
        p_starts_at: startsAt,
        p_weekdays: weekdays,
        p_interval_weeks: Number(intervalWeeks) || 1,
        p_until: until,
        p_location_id: locationId || null,
        p_therapist_id: selectedPatient.therapistId,
      });
      setBusy(false);
      if (seriesError) { setError(seriesError.message); return; }
      setNotice({
        tone: data.skipped.length ? 'warning' : 'success',
        created: data.created.length,
        skipped: data.skipped,
      });
    } else {
      // Which professional is part of the booking: the same person may see more
      // than one, and the server will not guess (migration 0040).
      const { data: booked, error: bookError } = await supabase.rpc('book_appointment', {
        p_dependent_id: selectedPatient.dependentId,
        p_starts_at: startsAt,
        p_location_id: locationId || null,
        p_therapist_id: selectedPatient.therapistId,
      });
      setBusy(false);
      if (bookError) { setError(bookError.message); return; }
      // Hours that need approval make a request, which waits for the
      // professional (0055).
      setNotice(booked?.status === 'requested'
        ? { tone: 'info', requested: true, created: 0, skipped: [] }
        : { tone: 'success', created: 1, skipped: [] });
    }

    closeBooking();
    loadSlots();
  };

  if (loading) return <Spinner />;

  // A patient with nobody to book for has nothing to look at. A professional
  // still has their own week, and can still block time in it, so the empty
  // roster is a notice rather than a dead end.
  if (isPatient && patients.length === 0) {
    return (
      <Card title={t('booking.titlePatient')}>
        <EmptyState
          icon={CalendarPlus}
          title={t('booking.noApprovedPatient')}
          description={t('booking.noApprovedPatientHint')}
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {isPatient ? t('booking.titlePatient') : t('booking.title')}
        </h1>
        <p className="text-sm text-slate-500">
          {isPatient ? t('booking.subtitlePatient') : t('booking.subtitleProfessional')}
        </p>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      {notice && (
        <Alert tone={notice.tone} title={t(notice.requested ? 'booking.requestSentTitle' : 'booking.bookedTitle')}>
          <p>
            {notice.requested
              ? t('booking.requestSentBody', {
                professional: selectedPatient?.therapists
                  ? `${selectedPatient.therapists.first_name} ${selectedPatient.therapists.last_name}`
                  : t('booking.yourProfessional'),
              })
              : t('booking.bookedCount', { count: notice.created })}
          </p>
          {notice.skipped.length > 0 && (
            <div className="mt-2">
              <p className="font-medium">{t('booking.skippedTitle')}</p>
              <ul className="mt-1 list-inside list-disc">
                {notice.skipped.map((item) => (
                  <li key={item.starts_at}>
                    {formatDate(item.starts_at)} {formatTime(item.starts_at)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Alert>
      )}

      {/* A patient may have different professionals for different family
          members, or more than one for the same person, so the week shown
          depends on which pair they pick. A professional is always looking at
          their own agenda, and a selector on top only suggested the grid was
          filtered by patient when it never was. */}
      {isPatient && (
        <Card>
          <Field label={t('appointments.patient')}>
            <Select value={selectedPatientId} onChange={(event) => setSelectedPatientId(event.target.value)}>
              {patients.map((patient) => (
                <option key={patient.key} value={patient.key}>
                  {patient.first_name} {patient.last_name}
                  {patient.therapists && ` · ${patient.therapists.first_name} ${patient.therapists.last_name}`}
                </option>
              ))}
            </Select>
          </Field>
        </Card>
      )}

      <div className="flex items-center justify-between gap-3">
        <Button variant="secondary" onClick={() => setWeekStart(addDaysToKey(weekStart, -7))}>
          <ChevronLeft size={16} />{t('booking.previousWeek')}
        </Button>
        <p className="text-sm font-medium text-slate-700">
          {formatDate(weekStart, { day: 'numeric', month: 'short' })} –{' '}
          {formatDate(addDaysToKey(weekStart, 6), { day: 'numeric', month: 'short' })}
        </p>
        <Button variant="secondary" onClick={() => setWeekStart(addDaysToKey(weekStart, 7))}>
          {t('booking.nextWeek')}<ChevronRight size={16} />
        </Button>
      </div>

      {!isPatient && patients.length === 0 && (
        <Alert tone="info">{t('booking.noPatients')}</Alert>
      )}

      {!isPatient && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-500">{t('slots.legend')}</p>
          <Button
            variant="secondary"
            disabled={patients.length === 0}
            onClick={() => {
              setCustomAt(`${weekStart}T09:00`);
              setLocationId(locations.find((location) => !location.is_online)?.id ?? locations[0]?.id ?? '');
              setBooking({ custom: true });
            }}
          >
            <Clock size={14} />{t('booking.customTime')}
          </Button>
        </div>
      )}

      {/* What a cancellation did shows beside the week it was made from, and
          gives way to a booking's notice. */}
      {isPatient && cancelNotice && !notice && <Alert tone="success">{cancelNotice}</Alert>}
      {isPatient && sessions?.length > 0 && (
        <p className="text-xs text-slate-500">{t('patientCancel.legend')}</p>
      )}

      {loadingSlots ? <Spinner /> : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
          {days.map((day) => (
            <div key={day.key} className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="mb-2 text-center text-xs font-semibold uppercase text-slate-500 first-letter:uppercase">
                {formatDate(day.key, { weekday: 'short' })}
              </p>
              <p className="mb-2 text-center text-lg font-bold text-slate-900">
                {formatDate(day.key, { day: 'numeric' })}
              </p>
              {day.entries.length === 0 ? (
                <p className="py-2 text-center text-xs text-slate-400">{t('booking.noSlots')}</p>
              ) : (
                <ul className="space-y-1">
                  {day.entries.map((entry) => {
                    if (entry.kind === 'session') {
                      const { session } = entry;
                      const name = `${session.dependents?.first_name ?? ''} ${session.dependents?.last_name ?? ''}`.trim();
                      const requested = session.status === 'requested';
                      const tone = requested ? 'bg-amber-100 text-amber-900' : 'bg-emerald-50 text-emerald-800';
                      const lines = (
                        <>
                          <span className="leading-tight">{formatTime(session.starts_at)}</span>
                          <span className="block truncate text-[11px] font-normal leading-tight opacity-80">{name}</span>
                        </>
                      );
                      // A patient's own session opens the cancel dialog (0057).
                      if (isPatient) {
                        const open = patientMayCancel(session);
                        return (
                          <li key={`session-${session.id}`}>
                            <button
                              type="button"
                              disabled={!open}
                              onClick={() => setCancelling(session)}
                              title={open
                                ? t(requested ? 'patientCancel.gridWithdrawHint' : 'patientCancel.gridHint', { name })
                                : name}
                              className={`flex h-11 w-full flex-col justify-center overflow-hidden rounded px-2 text-center text-sm font-medium transition ${tone} ${
                                open ? (requested ? 'hover:bg-amber-200' : 'hover:bg-emerald-100') : 'cursor-default'
                              }`}
                            >
                              {lines}
                            </button>
                          </li>
                        );
                      }
                      return (
                        <li key={`session-${session.id}`}>
                          <div
                            title={[
                              requested
                                ? `${t('appointments.status.requested')}: ${name}`
                                : t('slots.takenBy', { who: name }),
                              session.locations?.name,
                            ].filter(Boolean).join(' · ')}
                            className={`flex h-11 w-full flex-col justify-center overflow-hidden rounded px-2 text-center text-sm font-medium ${tone}`}
                          >
                            {lines}
                          </div>
                        </li>
                      );
                    }
                    const { slot } = entry;
                    // busy_label is null unless this is the caller's own
                    // agenda: a patient booking for their family never learns
                    // who else is in the diary.
                    const blocked = slot.busy_reason === 'blocked';
                    const title = slot.is_free
                      ? t(slot.requires_approval ? 'slots.freeToConfirm' : 'slots.free')
                      : slot.busy_label
                        ? t(blocked ? 'slots.blockedBy' : 'slots.takenBy', { who: slot.busy_label })
                        : t(blocked ? 'slots.blocked' : 'slots.taken');
                    return (
                      <li key={slot.slot_start}>
                        {/* One fixed height for every bubble: a slot that
                            happens to carry a name must not make its row taller
                            than the free ones beside it. */}
                        <button
                          disabled={!slot.is_free || patients.length === 0}
                          onClick={() => openSlot(slot)}
                          title={title}
                          className={`flex h-11 w-full flex-col justify-center overflow-hidden rounded px-2 text-sm font-medium transition ${
                            slot.is_free
                              // Hours that need approval, in another colour (0055).
                              ? (slot.requires_approval
                                ? 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200 hover:bg-amber-100'
                                : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100')
                              : blocked
                                ? 'cursor-not-allowed bg-slate-100 text-slate-400'
                                : 'cursor-not-allowed bg-emerald-50 text-emerald-800'
                          }`}
                        >
                          <span className="leading-tight">{formatTime(slot.slot_start)}</span>
                          {/* Whose slot it is, when the viewer is entitled to
                              know. Without it an unavailable slot looks like it
                              concerns somebody it has nothing to do with. */}
                          {/* A free time says where it is instead (0044). */}
                          {(slot.busy_label || slot.is_free) && (
                            <span className="block truncate text-[11px] font-normal leading-tight opacity-80">
                              {blocked ? <Ban size={10} className="mr-0.5 inline" /> : null}
                              {slot.busy_label ?? (slot.requires_approval
                                ? `${t('slots.toConfirm')} · ${placeLabel(slot)}`
                                : placeLabel(slot))}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Below the week, not above it: at the top it drew the eye before the
          week itself (15 Sep 2026). It still sits with the week it applies to,
          which Agenda del día, one day at a time, never allowed. */}
      {!isPatient && therapistId && (
        <ScheduleBlocks
          therapistId={therapistId}
          from={weekStart}
          to={addDaysToKey(weekStart, 6)}
          onChange={loadSlots}
        />
      )}

      <Modal
        open={Boolean(booking)}
        title={t('booking.confirmTitle')}
        onClose={closeBooking}
        footer={
          <>
            <Button variant="secondary" onClick={closeBooking}>
              {t('common.cancel')}
            </Button>
            <Button onClick={confirmBooking} loading={busy}>
              <Check size={16} />{t('booking.confirm')}
            </Button>
          </>
        }
      >
        {booking && (
          <div className="space-y-4">
            {booking.custom ? (
              <Field label={t('booking.customTimeLabel')} hint={t('booking.customTimeHint')} required>
                <Input
                  type="datetime-local"
                  value={customAt}
                  onChange={(event) => setCustomAt(event.target.value)}
                />
              </Field>
            ) : (
              <div className="rounded-lg bg-slate-50 p-4 text-sm">
                <p className="font-semibold text-slate-900">
                  {formatDate(booking.slot_start, { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                <p className="text-slate-600">
                  {formatTime(booking.slot_start)} – {formatTime(booking.slot_end)}
                </p>
                {selectedPatient?.therapists && (
                  <p className="mt-2 text-slate-600">
                    {selectedPatient.therapists.first_name} {selectedPatient.therapists.last_name}
                  </p>
                )}
              </div>
            )}

            {isPatient && booking.requires_approval && (
              <Alert tone="warning">{t('booking.needsApproval')}</Alert>
            )}

            {/* Asked here rather than above the week: these are what is being
                booked, and answering them before choosing a time made the grid
                look filtered by the patient. */}
            {!isPatient && (
              <Field label={t('booking.forPatient')} required>
                <Select
                  value={selectedPatientId}
                  onChange={(event) => setSelectedPatientId(event.target.value)}
                >
                  {patients.map((patient) => (
                    <option key={patient.key} value={patient.key}>
                      {patient.first_name} {patient.last_name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field label={t('availability.place')}>
              <Select value={locationId} onChange={(event) => setLocationId(event.target.value)}>
                {placeChoices.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </Select>
            </Field>

            {/* Standing series: a professional arranges it with the patient
                first; that conversation is not modelled. What is modelled is
                its shape — these days, every N weeks, until this date — and
                the count underneath, which is this dialog's arithmetic rather
                than a promise: an occupied date is skipped and reported. */}
            {!isPatient && (
              <Field label={t('booking.repeatLabel')} hint={t('booking.repeatHint')}>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={repeats}
                      onChange={(event) => setRepeats(event.target.checked)}
                    />
                    <Repeat size={16} className="text-slate-400" />
                    {t('booking.repeatOn')}
                  </label>

                  {repeats && (
                    <div className="space-y-3 border-l-2 border-slate-100 pl-3">
                      <div>
                        <p className="mb-1 text-xs font-medium text-slate-500">{t('booking.repeatDays')}</p>
                        <div className="flex flex-wrap gap-1">
                          {weekdayKeys.map((key, index) => {
                            const weekday = index + 1;
                            const chosen = weekdays.includes(weekday);
                            // The day of the session being booked cannot be
                            // taken out: the series contains it by definition.
                            const fixed = weekday === baseWeekday;
                            return (
                              <button
                                key={key}
                                type="button"
                                aria-pressed={chosen}
                                title={fixed ? t('booking.repeatFixedDay') : undefined}
                                onClick={() => {
                                  if (fixed) return;
                                  setExtraDays((current) => (current.includes(weekday)
                                    ? current.filter((day) => day !== weekday)
                                    : [...current, weekday]));
                                }}
                                className={`rounded-md border px-2 py-1 text-xs first-letter:uppercase ${chosen
                                  ? 'border-indigo-500 bg-indigo-50 font-semibold text-indigo-700'
                                  : 'border-slate-200 text-slate-600 hover:border-slate-300'} ${fixed ? 'cursor-default' : ''}`}
                              >
                                {formatDate(key, { weekday: 'short' })}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                        <span>{t('booking.repeatEvery')}</span>
                        <Input
                          type="number"
                          min="1"
                          max="8"
                          value={intervalWeeks}
                          onChange={(event) => setIntervalWeeks(event.target.value)}
                          className="w-20"
                        />
                        <span>{t('booking.weeks', { count: Number(intervalWeeks) || 1 })}</span>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                        <span>{t('booking.repeatUntil')}</span>
                        <Input
                          type="date"
                          value={until}
                          min={baseDay ?? undefined}
                          max={baseDay ? addDaysToKey(baseDay, 366) : undefined}
                          onChange={(event) => setUntil(event.target.value)}
                          className="w-44"
                        />
                      </div>

                      {seriesMessage && (
                        <p className="text-xs font-medium text-slate-700">{seriesMessage}</p>
                      )}
                    </div>
                  )}
                </div>
              </Field>
            )}

            {repeats && seriesDates.length > 1 && (
              <Alert tone="info">
                <span className="flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  {t('booking.repeatNotice')}
                </span>
              </Alert>
            )}
          </div>
        )}
      </Modal>

      {cancelling && (
        <PatientCancelModal
          session={cancelling}
          professionalName={selectedPatient?.therapists
            ? `${selectedPatient.therapists.first_name} ${selectedPatient.therapists.last_name}`
            : t('booking.yourProfessional')}
          onClose={() => setCancelling(null)}
          onDone={(message) => {
            setCancelling(null);
            setNotice(null);
            setCancelNotice(message);
            loadSlots();
          }}
        />
      )}
    </div>
  );
};

export default BookingView;
